import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import { Badge } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import AssignmentTurnedInOutlinedIcon from "@material-ui/icons/AssignmentTurnedInOutlined";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import BusinessOutlinedIcon from "@material-ui/icons/BusinessOutlined";
import AccountCircleOutlinedIcon from "@material-ui/icons/AccountCircleOutlined";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import SubjectOutlinedIcon from "@material-ui/icons/SubjectOutlined";
import BuildOutlinedIcon from "@material-ui/icons/BuildOutlined";
import DialpadOutlinedIcon from "@material-ui/icons/DialpadOutlined";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { InternalChatNotificationsContext } from "../context/InternalChatNotifications/InternalChatNotificationsContext";
import { Can } from "../components/Can";
import useCompanySipEnabled from "../hooks/useCompanySipEnabled";

const useStyles = makeStyles((theme) => ({
  navItem: {
    margin: "4px 12px",
    borderRadius: 16,
    paddingTop: 11,
    paddingBottom: 11,
    transition:
      "background-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(148, 163, 184, 0.10)"
          : "rgba(91, 100, 114, 0.08)",
      transform: "translateX(2px)",
    },
  },
  navItemActive: {
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(50, 184, 168, 0.14) 0%, rgba(23, 107, 99, 0.14) 100%)"
        : "linear-gradient(180deg, rgba(34, 135, 125, 0.10) 0%, rgba(23, 107, 99, 0.09) 100%)",
    boxShadow:
      theme.palette.type === "dark"
        ? "inset 0 0 0 1px rgba(80, 189, 176, 0.16)"
        : "inset 0 0 0 1px rgba(23, 107, 99, 0.12)",
  },
  navItemCompact: {
    justifyContent: "center",
    paddingLeft: 0,
    paddingRight: 0,
    margin: "6px 10px",
  },
  navIcon: {
    minWidth: 40,
    color: theme.palette.type === "dark" ? "#70d3c7" : theme.palette.primary.main,
  },
  navIconCompact: {
    minWidth: 0,
    margin: 0,
    justifyContent: "center",
  },
  navIconActive: {
    color: theme.palette.type === "dark" ? "#8de4da" : theme.palette.primary.dark,
  },
  navText: {
    "& .MuiTypography-root": {
      fontWeight: 650,
      color: theme.palette.text.primary,
      fontSize: "0.96rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  navTextActive: {
    "& .MuiTypography-root": {
      fontWeight: 700,
    },
  },
  navTextHidden: {
    display: "none",
  },
  subheader: {
    color: theme.palette.text.secondary,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontSize: 11,
    marginTop: 8,
  },
  subheaderHidden: {
    display: "none",
  },
}));

function ListItemLink(props) {
  const { icon, primary, to, className, compact } = props;
  const classes = useStyles();
  const location = useLocation();
  const isActive =
    to === "/"
      ? location.pathname === "/"
      : location.pathname === to || location.pathname.startsWith(`${to}/`);

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  const item = (
    <ListItem
      button
      component={renderLink}
      className={`${classes.navItem} ${isActive ? classes.navItemActive : ""} ${compact ? classes.navItemCompact : ""} ${className || ""}`.trim()}
    >
      {icon ? (
        <ListItemIcon
          className={`${classes.navIcon} ${isActive ? classes.navIconActive : ""} ${compact ? classes.navIconCompact : ""}`.trim()}
        >
          {icon}
        </ListItemIcon>
      ) : null}
      <ListItemText
        primary={primary}
        className={`${classes.navText} ${isActive ? classes.navTextActive : ""} ${compact ? classes.navTextHidden : ""}`.trim()}
      />
    </ListItem>
  );

  return <li>{compact ? <Tooltip title={primary} placement="right">{item}</Tooltip> : item}</li>;
}

const MainListItems = (props) => {
  const classes = useStyles();
  const { drawerClose, onOpenProfile, drawerOpen = true } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const { sipEnabled: isCompanySipEnabled } = useCompanySipEnabled();
  const { unreadCount } = useContext(InternalChatNotificationsContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const compact = !drawerOpen;

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  return (
    <div onClick={drawerClose}>
      {user.profile === "superadmin" ? (
        <Can
          role={user.profile}
          perform="drawer-superadmin-items:view"
          yes={() => (
            <>
              <ListItemLink
                to="/companies"
                primary="Empresas"
                icon={<BusinessOutlinedIcon />}
                compact={compact}
              />
              <Tooltip title="Meu Perfil" placement="right" disableHoverListener={!compact}>
                <ListItem
                  button
                  onClick={onOpenProfile}
                  className={`${classes.navItem} ${compact ? classes.navItemCompact : ""}`.trim()}
                >
                <ListItemIcon className={`${classes.navIcon} ${compact ? classes.navIconCompact : ""}`.trim()}>
                  <AccountCircleOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Meu Perfil"
                  className={`${classes.navText} ${compact ? classes.navTextHidden : ""}`.trim()}
                />
                </ListItem>
              </Tooltip>
            </>
          )}
        />
      ) : (
        <>
          <ListItemLink
            to="/"
            primary="Dashboard"
            icon={<DashboardOutlinedIcon />}
            compact={compact}
          />
          <ListItemLink
            to="/tickets"
            primary="Atendimento"
            icon={<WhatsAppIcon />}
            compact={compact}
          />
          <ListItemLink
            to="/internal-chat"
            primary="Chat interno"
            icon={
              <Badge badgeContent={unreadCount} color="secondary">
                <ForumOutlinedIcon />
              </Badge>
            }
            compact={compact}
          />
          <ListItemLink
            to="/internal-tickets"
            primary="Tickets internos"
            icon={<AssignmentOutlinedIcon />}
            compact={compact}
          />

          <ListItemLink
            to="/contacts"
            primary={i18n.t("mainDrawer.listItems.contacts")}
            icon={<ContactPhoneOutlinedIcon />}
            compact={compact}
          />
          <ListItemLink
            to="/quickAnswers"
            primary={i18n.t("mainDrawer.listItems.quickAnswers")}
            icon={<QuestionAnswerOutlinedIcon />}
            compact={compact}
          />
        </>
      )}
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider />
            <ListSubheader
              inset
              className={`${classes.subheader} ${compact ? classes.subheaderHidden : ""}`.trim()}
            >
              {i18n.t("mainDrawer.listItems.administration")}
            </ListSubheader>
            <ListItemLink
              to="/attendances"
              primary="Atendimentos"
              icon={<AssignmentTurnedInOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/api-tools"
              primary="API Tools"
              icon={<BuildOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/company"
              primary="Empresa"
              icon={<BusinessOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/connections"
              primary={i18n.t("mainDrawer.listItems.connections")}
              icon={
                <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
                  <SyncAltIcon />
                </Badge>
              }
              compact={compact}
            />
            <ListItemLink
              to="/queues"
              primary={i18n.t("mainDrawer.listItems.queues")}
              icon={<AccountTreeOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/users"
              primary={i18n.t("mainDrawer.listItems.users")}
              icon={<PeopleAltOutlinedIcon />}
              compact={compact}
            />
            {isCompanySipEnabled && (
              <ListItemLink
                to="/extensions"
                primary={i18n.t("mainDrawer.listItems.extensions")}
                icon={<DialpadOutlinedIcon />}
                compact={compact}
              />
            )}
            <ListItemLink
              to="/tags"
              primary="Tags"
              icon={<LocalOfferOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/ticket-subjects"
              primary="Assuntos do Chamado"
              icon={<SubjectOutlinedIcon />}
              compact={compact}
            />
            <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsOutlinedIcon />}
              compact={compact}
            />
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
